"use client"

import { useState, useEffect } from "react"
import { Navbar } from "@/components/ui/navbar"
import { FooterSection } from "@/components/sections/footer-section"
import { motion, AnimatePresence } from "framer-motion"
import { MapPin, ChevronDown, ChevronUp, Phone, Building2 } from "lucide-react"
import Image from "next/image"
import Link from "next/link"

interface Unit {
  code: string
  viewType: string
  description: string
  area: number | null
  pricePerMeter: number | null
  totalPrice: number | null
  floor: string
  status: string
}

interface Project {
  id: string
  name: string
  sheetName: string
  units: Unit[]
  imageFolder: string | null
}

function formatPrice(num: number) {
  return new Intl.NumberFormat("ar-EG").format(num)
}

function ProjectCard({ project }: { project: Project }) {
  const [isOpen, setIsOpen] = useState(false)
  const availableUnits = project.units.filter((u) => u.status === "available")
  const imagePath = project.imageFolder
    ? `/properties/${project.imageFolder}/img-1.jpg`
    : null

  return (
    <motion.div
      layout
      className="rounded-2xl border border-zinc-800/50 bg-zinc-900/50 overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between gap-4 text-right hover:bg-zinc-900/80 transition-colors"
      >
        <div className="flex items-center gap-4 flex-1">
          {imagePath ? (
            <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
              <Image src={imagePath} alt={project.name} fill className="object-cover" sizes="80px" />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-xl bg-brand/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-10 h-10 text-brand" />
            </div>
          )}
          <div>
            <h3 className="font-heading text-lg font-semibold text-zinc-100">{project.name}</h3>
            <p className="text-sm text-zinc-500 mt-1">
              {availableUnits.length} وحدة متاحة
            </p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-brand" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-400" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 border-t border-zinc-800/50 pt-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-400 border-b border-zinc-800">
                      <th className="py-3 px-2 text-right">كود الوحدة</th>
                      <th className="py-3 px-2 text-right">الوصف</th>
                      <th className="py-3 px-2 text-right">المساحة</th>
                      <th className="py-3 px-2 text-right">السعر الإجمالي</th>
                      <th className="py-3 px-2 text-right">الدور</th>
                      <th className="py-3 px-2 text-right">الإجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableUnits.map((unit) => (
                      <tr
                        key={unit.code}
                        className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                      >
                        <td className="py-3 px-2 text-zinc-300 font-mono text-xs">
                          {unit.code}
                        </td>
                        <td className="py-3 px-2 text-zinc-400">
                          <span className="text-brand text-xs">{unit.viewType}</span>
                          <span className="block text-zinc-300">{unit.description}</span>
                        </td>
                        <td className="py-3 px-2 text-zinc-400">
                          {unit.area ? `${unit.area} م²` : "-"}
                        </td>
                        <td className="py-3 px-2 text-zinc-300 font-medium">
                          {unit.totalPrice ? `${formatPrice(unit.totalPrice)} ج.م` : "-"}
                        </td>
                        <td className="py-3 px-2 text-zinc-500 text-xs">{unit.floor || "-"}</td>
                        <td className="py-3 px-2">
                          <Link
                            href={`https://wa.me/201212129070?text=مرحباً، أود الاستفسار عن الوحدة ${unit.code} في ${project.name}`}
                            target="_blank"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand hover:bg-brand-dark text-white text-xs font-medium transition-colors"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            استفسر
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default function UnitsPage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch("/data/units.json")
      .then((res) => res.json())
      .then((data) => {
        setProjects(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return (
    <main className="min-h-screen bg-zinc-950">
      <Navbar />
      <section className="pt-28 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-brand uppercase tracking-wider mb-4">
              المناطق والأحياء
            </p>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-zinc-100 mb-4">
              الوحدات العقارية المتاحة
            </h1>
            <p className="text-zinc-500 max-w-xl mx-auto text-balance">
              تصفح الوحدات المتاحة في مشاريعنا العقارية واختر ما يناسبك
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              جاري تحميل البيانات...
            </div>
          ) : (
            <div className="space-y-4">
              {projects
                .filter((p) => p.units.length > 0)
                .map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
            </div>
          )}

          <div className="mt-12 text-center">
            <Link
              href="https://wa.me/201212129070"
              target="_blank"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand hover:bg-brand-dark text-white font-medium transition-colors"
            >
              <Building2 className="w-5 h-5" />
              <span>تواصل معنا لمعرفة المزيد</span>
            </Link>
          </div>
        </div>
      </section>
      <FooterSection />
    </main>
  )
}
